import React from 'react';
import { motion } from 'framer-motion';
import { summaryMetrics, accountsData, initialPlaybooks } from '../data/mockData';
import HealthChart from '../components/charts/HealthChart';
import RiskPulse from '../components/ui/RiskPulse';
import StatusBadge from '../components/ui/StatusBadge';
import SafeIcon from '../common/SafeIcon';
import { FiTrendingUp, FiAlertCircle, FiShield, FiArrowRight, FiActivity } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const MetricCard = ({ title, value, icon, colorClass, subtitle }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#13131a] border border-white/5 rounded-2xl p-6 hover:border-white/10 transition-colors relative overflow-hidden group"
  >
    <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full blur-3xl opacity-10 group-hover:opacity-20 transition-opacity ${colorClass.split(' ')[0].replace('text-', 'bg-')}`}></div>
    <div className="flex justify-between items-start mb-4 relative z-10">
      <div>
        <h3 className="text-slate-400 text-sm font-medium">{title}</h3>
        <p className="text-3xl font-bold text-white mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-xl bg-white/5 ${colorClass}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
    </div>
    {subtitle && <p className="text-xs text-slate-500 mt-2">{subtitle}</p>}
  </motion.div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const atRiskAccounts = accountsData.filter(a => a.pulse === 'critical' || a.pulse === 'warning');
  const displayPlaybooks = initialPlaybooks.slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Signal Map</h1>
          <p className="text-slate-400 mt-2">Real-time telemetry of your customer portfolio health.</p>
        </div>
        <button onClick={() => navigate('/demo-center')} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-[0_0_15px_rgba(139,92,246,0.3)]">
          <SafeIcon icon={FiShield} />
          View Interactive Demo Guide
        </button>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard title="Total ARR Managed" value={summaryMetrics.totalRevenue} icon={FiTrendingUp} colorClass="text-emerald-400" subtitle="↑ 12% vs last quarter" />
        <MetricCard title="Net Retention Rate" value={summaryMetrics.nrr} icon={FiShield} colorClass="text-violet-400" subtitle="Top 10% industry benchmark" />
        <MetricCard title="Avg Health Score" value={summaryMetrics.avgHealthScore} icon={FiActivity} colorClass="text-emerald-400" subtitle="Stable across segments" />
        <MetricCard title="ARR At Risk" value={summaryMetrics.atRiskArr} icon={FiAlertCircle} colorClass="text-rose-400" subtitle={`${atRiskAccounts.length} accounts require attention`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 bg-[#13131a] border border-white/5 rounded-2xl p-6"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-white">Health vs Churn Risk Trajectory</h2>
            <select className="bg-[#0a0a0f] border border-white/10 text-slate-300 text-sm rounded-lg px-3 py-1.5 outline-none focus:border-violet-500">
              <option>Last 6 Months</option>
              <option>Year to Date</option>
            </select>
          </div>
          <HealthChart />
        </motion.div>

        {/* Risk Pulses Section */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#13131a] border border-white/5 rounded-2xl p-6 flex flex-col max-h-[400px]"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <SafeIcon icon={FiAlertCircle} className="text-rose-500" /> Active Risk Pulses
            </h2>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto pr-2 custom-scrollbar">
            {atRiskAccounts.map((account, idx) => (
              <div key={account.id} className="p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <RiskPulse level={account.pulse} />
                    <span className="font-medium text-slate-200">{account.name}</span>
                  </div>
                  <span className="text-xs font-mono text-slate-400">{account.arr}</span>
                </div>
                <p className="text-xs text-rose-400/80 mb-3 line-clamp-1">{account.riskReason}</p>
                <div className="flex justify-between items-center">
                  <StatusBadge health={account.health} />
                  <button onClick={() => navigate('/accounts')} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-colors">
                    View Context <SafeIcon icon={FiArrowRight} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Playbooks Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-[#13131a] border border-white/5 rounded-2xl p-6"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-white">Suggested Playbooks</h2>
          <button onClick={() => navigate('/playbooks')} className="text-sm text-violet-400 hover:text-violet-300 transition-colors flex items-center gap-1">
            View All simulated states <SafeIcon icon={FiArrowRight} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {displayPlaybooks.map(playbook => (
            <div key={playbook.id} className="p-5 rounded-xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 hover:border-violet-500/30 transition-all group">
              <div className="flex justify-between items-start mb-4">
                <span className="text-xs px-2 py-1 rounded-md bg-white/5 text-slate-300 border border-white/10">{playbook.type}</span>
                <span className={`text-xs font-medium ${playbook.impact === 'Critical' ? 'text-rose-400' : playbook.impact === 'High' ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {playbook.impact} Impact
                </span>
              </div>
              <h3 className="font-medium text-slate-200 mb-1 group-hover:text-violet-400 transition-colors">{playbook.title}</h3>
              <p className="text-sm text-slate-500 mb-4">For: {playbook.account}</p>
              <button onClick={() => navigate('/playbooks')} className="w-full py-2 rounded-lg bg-white/5 hover:bg-violet-500/20 text-sm text-slate-300 hover:text-violet-300 transition-colors border border-white/5 hover:border-violet-500/30">
                Execute Playbook
              </button>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;